import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Alert, Box, Button, CircularProgress, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import { checkInSubTask, checkInTicket, checkOutSiteVisit, deleteSiteVisitPhoto, getSubTaskSiteVisits, getTicketSiteVisits, uploadSiteVisitPhoto } from '../../../Redux/actions';
import { THEME_COLOR, buttonStyles } from '../../../theme/formStyles';
import { engineerName, fmtDate, fmtDuration, fmtTime, isOnDay, isOpenVisit, todayYmd, visitMinutes } from '../projectSchedule.utils';
import { downloadVisitReport, printVisitReport } from './visitReport';

const MIN_NOTES = 10;
const MAX_PHOTO_EDGE = 1600;
const sectionSx = { fontSize: '11.5px', fontWeight: 700, color: '#6b7785', textTransform: 'uppercase', letterSpacing: '.05em', mt: 2.25, mb: 0.75 };
const fieldSx = { '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: THEME_COLOR }, '& .MuiInputLabel-root.Mui-focused': { color: THEME_COLOR } };
const sameUser = (a, b) => !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

// Shrink phone photos before upload; anything that can't be decoded is sent as-is
const resizeImage = (file) => new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
        const scale = Math.min(1, MAX_PHOTO_EDGE / Math.max(img.width, img.height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(url);
        canvas.toBlob(b => resolve(b ? new File([b], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' }) : file), 'image/jpeg', 0.82);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
});

// The check-out report draft survives closing the dialog (per browser tab)
const draftKey = (visitId) => `pmVisitDraft:${visitId}`;
const readDraft = (visitId) => {
    try { return JSON.parse(sessionStorage.getItem(draftKey(visitId)) || 'null') || {}; } catch { return {}; }
};
const writeDraft = (visitId, draft) => {
    try { sessionStorage.setItem(draftKey(visitId), JSON.stringify(draft)); } catch { /* storage unavailable */ }
};
const clearDraft = (visitId) => {
    try { sessionStorage.removeItem(draftKey(visitId)); } catch { /* storage unavailable */ }
};

export function TimesGrid({ visit }) {
    const cells = [
        { label: 'Check in', value: visit ? fmtTime(visit.check_in_at) : '—' },
        { label: 'Check out', value: visit ? fmtTime(visit.check_out_at) : '—' },
        { label: 'Time on site', value: visit ? fmtDuration(visitMinutes(visit)) : '—', hl: true },
    ];
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1, mb: 1.5 }}>
            {cells.map(c => (
                <Box key={c.label} sx={{ bgcolor: c.hl ? '#e8f7ee' : '#fff', border: '1px solid', borderColor: c.hl ? '#bfe3d8' : '#e5e9ee', borderRadius: 2.5, px: 1.25, py: 1 }}>
                    <Typography sx={{ fontSize: '11px', color: '#6b7785', textTransform: 'uppercase', letterSpacing: '.04em', fontWeight: 700 }}>{c.label}</Typography>
                    <Typography sx={{ fontSize: '16px', fontWeight: 500, color: c.hl ? '#16a34a' : '#1f2933' }}>{c.value}</Typography>
                </Box>
            ))}
        </Box>
    );
}

function PhotoGrid({ photos, onRemove, busyId }) {
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1 }}>
            {photos.map(p => (
                <Box key={p.id} sx={{ position: 'relative' }}>
                    <Box component="img" src={p.url} alt="Site photo" sx={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', borderRadius: 2, border: '1px solid #e5e9ee', display: 'block' }} />
                    {onRemove && (
                        <IconButton size="small" onClick={() => onRemove(p)} disabled={busyId === p.id} aria-label="Remove photo"
                            sx={{ position: 'absolute', top: 4, right: 4, width: 24, height: 24, bgcolor: 'rgba(0,0,0,.6)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,.75)' } }}>
                            {busyId === p.id ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : <CloseIcon sx={{ fontSize: 14 }} />}
                        </IconButton>
                    )}
                </Box>
            ))}
        </Box>
    );
}

// Today's visit (check in → photos + report → check out) and the visit log for one sub-task.
// Only the sub-task's SPOC (or the ticket's engineer) can check in; everyone on the team sees the times and reports.
// task is a schedule item: a sub-task, or a ticket visit (kind 'ticket', id = ticket id).
export default function VisitPanel({ task, currentUserId, onChanged }) {
    const dispatch = useDispatch();
    const [visits, setVisits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(null); // 'checkin' | 'upload' | 'checkout' | photo id
    const [draft, setDraft] = useState({});
    const [, setTick] = useState(0);

    const today = todayYmd();
    const isTicket = task.kind === 'ticket';
    const isSpoc = sameUser(task.spoc?.id, currentUserId);
    const scheduledToday = isOnDay(task, today);
    const closedTask = ['Completed', 'Cancelled'].includes(task.status);
    const myToday = visits.find(v => v.visit_date === today && sameUser(v.user_id, currentUserId));
    const todayVisit = myToday || visits.find(v => v.visit_date === today);
    const done = visits.filter(v => v.check_out_at);
    const totalMinutes = visits.reduce((s, v) => s + visitMinutes(v), 0);

    const load = useCallback(() => {
        setLoading(true);
        dispatch(isTicket ? getTicketSiteVisits(task.id) : getSubTaskSiteVisits(task.id)).then((res) => {
            if (res?.data?.success) { setVisits(res.data.data || []); setError(null); }
            else setError(res?.data?.message || 'Could not load site visits');
            setLoading(false);
        }).catch(() => { setError('Could not load site visits'); setLoading(false); });
    }, [dispatch, task.id, isTicket]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (myToday && isOpenVisit(myToday)) setDraft(readDraft(myToday.id));
    }, [myToday?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep the "time on site" counter moving while a visit is open
    useEffect(() => {
        if (!visits.some(isOpenVisit)) return undefined;
        const h = setInterval(() => setTick(t => t + 1), 30000);
        return () => clearInterval(h);
    }, [visits]);

    const replaceVisit = (visit) => setVisits(list => [visit, ...list.filter(v => v.id !== visit.id)]);

    const handleCheckIn = () => {
        setBusy('checkin'); setError(null);
        dispatch(isTicket ? checkInTicket(task.id) : checkInSubTask(task.id)).then((res) => {
            setBusy(null);
            if (res?.data?.success) { replaceVisit({ photos: [], ...res.data.data }); onChanged(); }
            else setError(res?.data?.message || 'Check-in failed');
        });
    };

    const handleAddPhotos = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (!files.length || !myToday) return;
        setBusy('upload'); setError(null);
        for (const file of files) {
            const formData = new FormData();
            formData.append('file', await resizeImage(file));
            const res = await dispatch(uploadSiteVisitPhoto(myToday.id, formData));
            if (!res?.data?.success) { setError(res?.data?.message || `Could not upload ${file.name}`); break; }
            setVisits(list => list.map(v => (v.id === myToday.id ? { ...v, photos: [...(v.photos || []), res.data.data] } : v)));
        }
        setBusy(null);
    };

    const handleRemovePhoto = (photo) => {
        setBusy(photo.id); setError(null);
        dispatch(deleteSiteVisitPhoto(photo.id)).then((res) => {
            setBusy(null);
            if (res?.data?.success) setVisits(list => list.map(v => (v.id === myToday.id ? { ...v, photos: v.photos.filter(p => p.id !== photo.id) } : v)));
            else setError(res?.data?.message || 'Could not remove the photo');
        });
    };

    const updateDraft = (field, value) => {
        const next = { ...draft, [field]: value };
        setDraft(next);
        writeDraft(myToday.id, next);
    };

    const handleCheckOut = () => {
        setBusy('checkout'); setError(null);
        dispatch(checkOutSiteVisit(myToday.id, draft)).then((res) => {
            setBusy(null);
            if (res?.data?.success) { clearDraft(myToday.id); setDraft({}); replaceVisit(res.data.data); onChanged(); }
            else setError(res?.data?.message || 'Check-out failed');
        });
    };

    if (loading && !visits.length) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} sx={{ color: THEME_COLOR }} /></Box>;
    }

    const photos = myToday?.photos || [];
    const missing = [];
    if (!photos.length) missing.push('at least 1 photo');
    if ((draft.notes || '').trim().length < MIN_NOTES) missing.push(`work done (${MIN_NOTES}+ characters)`);

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}

            {scheduledToday && (
                <>
                    <Typography sx={sectionSx}>Today's site visit · {fmtDate(today)}</Typography>
                    <Box sx={{ borderRadius: 3.5, p: 1.75, background: 'linear-gradient(135deg,#f3fbf9,#fff)', border: '1px solid #cfe9e3' }}>
                        {todayVisit && !myToday && (
                            <Typography sx={{ fontSize: '13px', color: '#6b7785', mb: 1 }}>{todayVisit.user_name}</Typography>
                        )}
                        <TimesGrid visit={todayVisit} />

                        {!todayVisit && isSpoc && !closedTask && (
                            <>
                                <Button fullWidth variant="contained" disabled={busy === 'checkin'} onClick={handleCheckIn}
                                    sx={{ ...buttonStyles.contained, bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' }, py: 1.4, fontWeight: 700, borderRadius: 3 }}>
                                    {busy === 'checkin' ? <CircularProgress size={22} color="inherit" /> : '✓ Check in at site'}
                                </Button>
                                <Typography sx={{ fontSize: '12px', color: '#6b7785', textAlign: 'center', mt: 1 }}>Your arrival time is recorded when you tap.</Typography>
                            </>
                        )}
                        {!todayVisit && !isSpoc && (
                            <Typography sx={{ fontSize: '12.5px', color: '#6b7785', textAlign: 'center' }}>
                                {task.spoc ? `${engineerName(task)} hasn't checked in yet. Only the ${isTicket ? 'assigned engineer' : 'sub-task SPOC'} can check in.` : 'Assign a SPOC to this sub-task so they can check in.'}
                            </Typography>
                        )}
                        {!todayVisit && isSpoc && closedTask && (
                            <Typography sx={{ fontSize: '12.5px', color: '#6b7785', textAlign: 'center' }}>This {isTicket ? 'ticket' : 'sub-task'} is {isTicket ? 'resolved' : task.status.toLowerCase()}.</Typography>
                        )}

                        {myToday && isOpenVisit(myToday) && (
                            <Box sx={{ bgcolor: '#fff', border: '1px solid #e5e9ee', borderRadius: 3, p: 1.5 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '13.5px', mb: 1 }}>
                                    📋 Site visit report <span style={{ fontWeight: 400, color: '#6b7785' }}>· needed before check-out</span>
                                </Typography>
                                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#6b7785', mb: 0.5 }}>Site photos <span style={{ color: '#dc2626' }}>*</span></Typography>
                                {photos.length > 0 && <PhotoGrid photos={photos} onRemove={handleRemovePhoto} busyId={busy} />}
                                <Button component="label" variant="outlined" size="small" disabled={busy === 'upload'} sx={{ ...buttonStyles.outlined, mt: 1 }}
                                    startIcon={busy === 'upload' ? <CircularProgress size={14} sx={{ color: THEME_COLOR }} /> : <AddAPhotoIcon />}>
                                    {busy === 'upload' ? 'Uploading…' : 'Add photos'}
                                    <input hidden type="file" accept="image/*" capture="environment" multiple onChange={handleAddPhotos} />
                                </Button>

                                <TextField fullWidth multiline minRows={3} size="small" label="Work done today *" value={draft.notes || ''}
                                    onChange={e => updateDraft('notes', e.target.value)} placeholder="What was installed, tested or fixed today" sx={{ ...fieldSx, mt: 1.75 }} />
                                <TextField fullWidth multiline minRows={2} size="small" label="Issues / observations" value={draft.issues || ''}
                                    onChange={e => updateDraft('issues', e.target.value)} sx={{ ...fieldSx, mt: 1.5 }} />
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25, mt: 1.5 }}>
                                    <TextField size="small" label="Next steps" value={draft.next_steps || ''} onChange={e => updateDraft('next_steps', e.target.value)} sx={fieldSx} />
                                    <TextField size="small" label="Client representative" value={draft.client_rep || ''} onChange={e => updateDraft('client_rep', e.target.value)} sx={fieldSx} />
                                </Box>

                                <Button fullWidth variant="contained" disabled={missing.length > 0 || busy === 'checkout' || busy === 'upload'} onClick={handleCheckOut}
                                    sx={{ mt: 1.75, py: 1.4, fontWeight: 700, borderRadius: 3, bgcolor: '#1f2933', '&:hover': { bgcolor: '#111827' } }}>
                                    {busy === 'checkout' ? <CircularProgress size={22} color="inherit" /> : 'Submit report & check out'}
                                </Button>
                                <Typography sx={{ fontSize: '12px', color: '#6b7785', textAlign: 'center', mt: 1 }}>
                                    {missing.length ? `Still needed: ${missing.join(' and ')}` : 'Ready to check out.'}
                                </Typography>
                            </Box>
                        )}

                        {todayVisit && !isOpenVisit(todayVisit) && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Typography sx={{ fontSize: '13px', color: '#6b7785', flex: 1 }}>Visit finished · report saved</Typography>
                                <Button size="small" variant="outlined" sx={buttonStyles.outlined} onClick={() => downloadVisitReport(task, visits, today)}>⬇ Today's report</Button>
                            </Box>
                        )}
                        {todayVisit && isOpenVisit(todayVisit) && !myToday && (
                            <Typography sx={{ fontSize: '12.5px', color: '#16a34a', fontWeight: 700 }}>● On site now</Typography>
                        )}
                    </Box>
                </>
            )}

            {visits.length > 0 && (
                <>
                    <Typography sx={sectionSx}>
                        Site visit log <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>· {visits.length} visit{visits.length === 1 ? '' : 's'} · {fmtDuration(totalMinutes)}</span>
                    </Typography>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {['Date', 'Engineer', 'In', 'Out', 'Hours', ''].map(h => (
                                        <TableCell key={h} sx={{ fontSize: '11px', color: '#6b7785', fontWeight: 700, textTransform: 'uppercase', px: 0.5 }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visits.slice(0, 15).map(v => (
                                    <TableRow key={v.id} sx={v.visit_date === today ? { bgcolor: '#f3fbf9' } : undefined}>
                                        <TableCell sx={{ px: 0.5, whiteSpace: 'nowrap' }}>{fmtDate(v.visit_date, 'D MMM')}</TableCell>
                                        <TableCell sx={{ px: 0.5 }}>{v.user_name}</TableCell>
                                        <TableCell sx={{ px: 0.5, whiteSpace: 'nowrap' }}>{fmtTime(v.check_in_at)}</TableCell>
                                        <TableCell sx={{ px: 0.5, whiteSpace: 'nowrap' }}>{v.check_out_at ? fmtTime(v.check_out_at) : <span style={{ color: '#16a34a', fontWeight: 700 }}>on site</span>}</TableCell>
                                        <TableCell sx={{ px: 0.5, whiteSpace: 'nowrap' }}>{fmtDuration(visitMinutes(v))}</TableCell>
                                        <TableCell sx={{ px: 0.5, textAlign: 'right' }}>
                                            {v.check_out_at && (
                                                <Button size="small" sx={{ color: THEME_COLOR, textTransform: 'none', minWidth: 0 }} onClick={() => downloadVisitReport(task, visits, v.visit_date)}>⬇ Report</Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                    {visits.length > 15 && <Typography sx={{ fontSize: '12px', color: '#6b7785', mt: 0.5 }}>Showing the latest 15 visits. The full report includes all of them.</Typography>}
                    {done.length > 0 && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                            <Button size="small" variant="outlined" sx={buttonStyles.outlined} onClick={() => downloadVisitReport(task, visits)}>⬇ Download full report</Button>
                            <Button size="small" variant="outlined" sx={buttonStyles.outlined} onClick={() => printVisitReport(task, visits)}>🖨 Print / Save PDF</Button>
                        </Box>
                    )}
                </>
            )}

            {!scheduledToday && !visits.length && (
                <Typography sx={{ fontSize: '12.5px', color: '#6b7785', mt: 2 }}>
                    No site visits yet. Check-in opens on the scheduled days ({fmtDate(task.start_date, 'D MMM')} – {fmtDate(task.end_date, 'D MMM')}).
                </Typography>
            )}
        </Box>
    );
}
